import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { Bell } from '@phosphor-icons/react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NotificationBell() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const ref = useRef(null);

  useEffect(() => {
    if (!user) return;
    fetchCount();
    const interval = setInterval(fetchCount, 20000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchCount = async () => {
    try {
      const { data } = await axios.get(`${API}/notifications/unread-count`, { withCredentials: true });
      setCount(data.count);
    } catch {}
  };

  const toggleOpen = async () => {
    if (!open) {
      try {
        const { data } = await axios.get(`${API}/notifications?limit=10`, { withCredentials: true });
        setNotifications(data);
      } catch {}
    }
    setOpen(!open);
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { withCredentials: true });
      setCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch {}
  };

  if (!user) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={toggleOpen}
        className="relative p-2 text-[#6A6A6A] hover:text-[#11100F] transition-colors"
        data-testid="notification-bell"
      >
        <Bell size={22} />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#534AB7] rounded-full text-white text-[10px] flex items-center justify-center font-bold">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-[#E5E5E5] rounded-sm shadow-lg z-50 animate-fade-in">
          <div className="flex items-center justify-between p-3 border-b border-[#E5E5E5]">
            <span className="text-sm font-bold text-[#11100F]">
              {lang === 'de' ? 'Benachrichtigungen' : 'Notifications'}
            </span>
            {count > 0 && (
              <button onClick={markAllRead} className="text-xs text-[#534AB7] font-semibold hover:underline" data-testid="mark-all-read">
                {lang === 'de' ? 'Alle gelesen' : 'Mark all read'}
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <p className="p-4 text-sm text-[#6A6A6A] text-center">
                {lang === 'de' ? 'Keine Benachrichtigungen' : 'No notifications'}
              </p>
            ) : (
              notifications.map(n => (
                <a
                  key={n.id}
                  href={n.link || '#'}
                  className={`block p-3 border-b border-[#F3F3F3] hover:bg-[#FAFAFA] transition-colors ${!n.read ? 'bg-blue-50/50' : ''}`}
                  data-testid={`notification-${n.id}`}
                >
                  <p className="text-sm font-semibold text-[#11100F]">{n.title}</p>
                  <p className="text-xs text-[#6A6A6A] mt-0.5">{n.message}</p>
                  <p className="text-xs text-[#CCCCCC] mt-1">
                    {new Date(n.created_at).toLocaleString(lang === 'de' ? 'de-DE' : 'en-US', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </a>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
